import * as React from 'react';

import { Button, useMemoDataGridRows } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../_state/financeInvoices.state';
import * as formState from '../../_state/invoiceForm.state';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { InvoiceRecord } from '~/modules/finance/types';

//
// ⚛️ ⚛️ ⚛️ INVOICES LIST ACTIONS ⚛️ ⚛️ ⚛️
//
export const InvoicesListActions = React.memo(() => {
  // Translations
  const t = useTranslations();

  // Atoms
  const { data: { invoices } = { invoices: [] } } = useAtomValue(
    state.$remoteInvoices,
  );
  const selectedKeys = useAtomValue(state.$selectedKeys);
  const setInvoicesToReceive = useSetAtom(state.$invoicesToReceive);
  const setIsPaymentDrawerOpen = useSetAtom(state.$isPaymentDrawerOpen);
  const setIsWizardOpen = useSetAtom(state.$isInvoiceWizardOpen);
  const resetInvoiceForm = useSetAtom(formState.$resetInvoiceForm);

  // Data hooks
  const toast = useToast();
  const selectedRows = useMemoDataGridRows(invoices, selectedKeys);

  // Callbacks
  const handleReceive = React.useCallback(
    (invoicesToPay: InvoiceRecord[]) => {
      const allPending = invoicesToPay.every(
        (invoice) => invoice.status === 'Pending',
      );
      if (!allPending) {
        toast.error(t.invoices.list.tooltips.notAllPending);
        return;
      }
      setInvoicesToReceive(invoicesToPay);
      setIsPaymentDrawerOpen(true);
    },
    [toast, t, setInvoicesToReceive, setIsPaymentDrawerOpen],
  );

  const handleCreateClick = React.useCallback(() => {
    resetInvoiceForm();
    setIsWizardOpen(true);
  }, [resetInvoiceForm, setIsWizardOpen]);

  return (
    <>
      {selectedRows.length > 0 && (
        <Button
          color="primary"
          variant="outline"
          onPress={() => handleReceive(selectedRows)}
        >
          {t.invoices.list.buttons.receiveMultiple(selectedRows.length)}
        </Button>
      )}
      <Button
        color="primary"
        variant="solid"
        icon={<LucidePlusCircle className="size-4" />}
        onPress={handleCreateClick}
      >
        {t.invoices.list.buttons.create}
      </Button>
    </>
  );
});
